import { Box, Stack, Typography } from "@mui/material";

import { IPanelHomeCnpj } from "../contracts/home.types";
import { formatFederalTaxId } from "../format";
import { HOME_TOKENS } from "./tokens";
import { StateIcon } from "./StateIcon";

/**
 * Grade compartilhada da linha do setor "Saudáveis". Mesma ideia de
 * `CNPJ_ROW_GRID` em `PortfolioPage.tsx`: uma definição só, pra `EM_DIA` e
 * `PARADO` não saírem de alinhamento uma da outra.
 */
const HEALTHY_ROW_GRID = "minmax(160px, 2fr) 168px minmax(120px, 1.4fr) minmax(120px, 1fr)";

interface IHealthyRowProps {
  cnpj: IPanelHomeCnpj;
  onOpen: (cnpj: IPanelHomeCnpj) => void;
  /**
   * Libera o eixo de SUCESSO (régua verde em opacidade baixa) para `EM_DIA`.
   * Quem decide é a ZONA, não o estado: `EM_DIA` também aparece na zona "Em
   * configuração", que é informativa — contrato em configuração entregando não
   * é conquista, é o esperado. `false` por padrão: cor a mais tem que ser
   * pedida, nunca herdada por descuido de quem adicionar uma zona nova.
   */
  highlightSuccess?: boolean;
}

/**
 * `EM_DIA` -> "Em dia"; `PARADO` -> três leituras possíveis, nunca alarme,
 * sempre em `text.secondary`/`success.main`:
 *   - já entregou alguma vez (`stalledForDays` presente) -> contagem de dias,
 *     como antes do fix do Blocker 2;
 *   - nunca entregou e a fonte CONFIRMA (`neverSucceeded === true`) -> diz
 *     isso sem rodeio;
 *   - nunca entregou mas a fonte não pôde ser lida (nem `stalledForDays` nem
 *     `neverSucceeded` presentes) -> NÃO pode afirmar "nunca entregou", que
 *     seria um fato não confirmado; diz que o painel não conseguiu confirmar.
 * Fundamentado no fix do Blocker 2 (review final v2.1, 2026-08-20): antes,
 * as três situações colapsavam no mesmo sentinela e a diferença entre
 * "nunca teve sucesso" e "não consegui ler a fonte" se perdia na tela.
 */
function stateLabel(cnpj: IPanelHomeCnpj): string {
  if (cnpj.state === "PARADO") {
    const days = cnpj.stalledForDays;
    if (days !== undefined) return `Parado há ${days} dia${days === 1 ? "" : "s"}`;
    if (cnpj.neverSucceeded) return "Parado — nunca entregou";
    return "Parado — não foi possível confirmar";
  }
  if (cnpj.state === "EM_DIA") return "Em dia";
  /**
   * `CONDUCCO` normalmente nunca chega nesta linha (vai pra `ConduccoRow`, com
   * régua vermelha) — exceto num caso: contrato EM_CONFIGURACAO, que sai do
   * setor de alarme por decisão de produto ("em configuração não alarma e não
   * conta como tarefa nossa"). A falha não pode virar "Não reconhecida", que
   * seria falso: ela foi reconhecida, só não gera tarefa ainda. Diz o fato, em
   * `text.secondary` como o resto da linha — o contexto de por que não alarma
   * vive no cabeçalho da zona.
   */
  if (cnpj.state === "CONDUCCO") return "Falha registrada";
  /**
   * Achado D (review final v2.1, 2026-08-20): o ramo final era `return "Em
   * dia"`, o que fazia qualquer `state` novo do backend — os dois repos não
   * fazem deploy atômico — sair rotulado como saudável. Falso verde é o pior
   * defeito desta tela, então o estado que a tela não conhece se declara.
   */
  return "Não reconhecida";
}

/**
 * Linha única do setor "Saudáveis" (T8): cliente · CNPJ · corretora ·
 * situação, em grade de 4 colunas. `PARADO` aparece sem alarme — ícone
 * cinza de pausa e contagem de dias, nunca vermelho: é a régua vermelha do
 * setor "Exige a Conducco" que carrega o alarme desta tela, não esta linha.
 */
export function HealthyRow({ cnpj, onOpen, highlightSuccess = false }: IHealthyRowProps) {
  const stateKey = cnpj.state === "PARADO" ? "PARADO" : "EM_DIA";
  /** Achado D: `state` fora do contrato não tem ícone (nenhum dos dois o descreve) e nunca herda a cor de `EM_DIA`. */
  const estadoConhecido = cnpj.state === "EM_DIA" || cnpj.state === "PARADO";
  const stateColor = estadoConhecido ? HOME_TOKENS.state[stateKey].color : "text.secondary";
  const rotuloEstado = stateLabel(cnpj);
  /**
   * Eixo de SUCESSO — "os saudáveis podem ter um pouco mais de cor"
   * (2026-08-20). Só `EM_DIA`, e só onde a zona libera: `PARADO` não é sucesso
   * (pode ser onboarding, contrato pausado — não alarma, mas também não é
   * conquista), `state` desconhecido não é classificável, e "Em configuração" é
   * informativo. A régua é pseudo-elemento posicionado: não entra no fluxo, não
   * desloca a grade e não desalinha `EM_DIA` de `PARADO`.
   */
  const reguaDeSucesso = highlightSuccess && cnpj.state === "EM_DIA";

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(cnpj)}
      sx={{
        display: "grid",
        gridTemplateColumns: HEALTHY_ROW_GRID,
        alignItems: "center",
        columnGap: 2,
        width: "100%",
        position: "relative",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        cursor: "pointer",
        border: 0,
        bgcolor: "transparent",
        minHeight: HOME_TOKENS.row.minHeight,
        px: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        transition: `background-color ${HOME_TOKENS.motion.hoverMs}ms`,
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: HOME_TOKENS.accent,
          outlineOffset: 2,
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {reguaDeSucesso && (
        <Box
          component="span"
          aria-hidden
          data-testid="regua-de-sucesso"
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            display: "block",
            width: `${HOME_TOKENS.row.railWidth}px`,
            bgcolor: HOME_TOKENS.state.EM_DIA.successRail,
            opacity: HOME_TOKENS.state.EM_DIA.successRailOpacity,
          }}
        />
      )}

      <Typography variant="body2" fontWeight={500} noWrap title={cnpj.buyerName}>
        {cnpj.buyerName}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        title={formatFederalTaxId(cnpj.federalTaxId)}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {formatFederalTaxId(cnpj.federalTaxId)}
      </Typography>

      <Typography variant="body2" color="text.secondary" noWrap title={cnpj.sellerName}>
        {cnpj.sellerName}
      </Typography>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {estadoConhecido && <StateIcon state={stateKey} />}
        {/* Achado E: era o único texto `noWrap` da linha sem `title`, e é onde a frase mais longa vive. */}
        <Typography variant="body2" color={stateColor} noWrap title={rotuloEstado}>
          {rotuloEstado}
        </Typography>
      </Stack>
    </Box>
  );
}
